"use client";

import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HeaderMain from "../dasboard-component/Components/HeaderMain";
import { useAuth } from "../context/AuthContext";
import { useInstituteAuth } from "../context/InstituteAuthContext";
import { toast } from "react-toastify";

/**
 * JSON shape we persist per golf game
 * {
 *   gameMode: "singles",
 *   player1: { name, strokes: number[], total, playerId },
 *   player2: { name, strokes: number[], total, playerId },
 *   pars: number[], // length 18
 *   holes: number, // holes completed
 *   winner: "player1" | "player2" | "draw"
 * }
 */

const getScoreStatus = (strokes, par) => {
  const diff = strokes - par;
  if (diff === -2) return "Eagle 🦅";
  if (diff === -1) return "Birdie 🐦";
  if (diff === 0) return "Par ⛳";
  if (diff === 1) return "Bogey 😬";
  if (diff >= 2) return "Double+ Bogey 😩";
  return "-";
};

const tips = [
  "Tip: Focus on accuracy over power on short holes.",
  "Tip: Avoid hazards by aiming for the fairway.",
  "Tip: Line up putts from multiple angles.",
  "Tip: Use a higher loft for bunker shots.",
  "Tip: Play smart on Par 5s — layup if needed.",
];

const brandBlue = "#3498db";
const brandBlueDark = "#2980b9";

export const AddGolf = () => {
  const location = useLocation();
  const data = location.state || {};
  const navigate = useNavigate();

  const [currentHole, setCurrentHole] = useState(1);
  const [pars, setPars] = useState(Array(18).fill(4));
  const [gameMode] = useState("singles"); // Golf is always singles (1v1)
  const [player1, setPlayer1] = useState({
    name: "Player 1",
    strokes: [],
    total: 0,
    playerId: null,
  });
  const [player2, setPlayer2] = useState({
    name: "Player 2",
    strokes: [],
    total: 0,
    playerId: null,
  });
  const [activePlayer, setActivePlayer] = useState("player1");
  const [autoSwitch, setAutoSwitch] = useState(false);
  const [history, setHistory] = useState([]);
  const [timer, setTimer] = useState(0);
  const [isTiming, setIsTiming] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const timerRef = useRef(null);
  const { user } = useAuth();
  const { instituteUser } = useInstituteAuth();

  // Check if this is institute context
  const isInstitute = data.isInstitute || false;
  const instituteId = data.instituteId || instituteUser?.id;

  // Fetch available users for player selection
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/users`,
          { credentials: "include" }
        );
        if (response.ok) {
          const users = await response.json();

          // Get current user ID
          const currentUserId = user?.uid;

          // Prioritize invited users and show their status
          const invitedUsers = (data.invitedUsers || []).map((user) => ({
            ...user,
            isInvited: true,
            invitationStatus: user.status,
          }));

          // Find current user and add them if not already in invited users
          const currentUser = users.find((user) => user.id == currentUserId);
          const currentUserIsInvited = invitedUsers.some(
            (invited) => invited.user_id == currentUserId
          );

          let finalUserList = [...invitedUsers];

          // Add current user if not already invited
          if (currentUser && !currentUserIsInvited) {
            finalUserList.push({
              ...currentUser,
              isCurrent: true,
            });
          }

          // Filter out invited users AND current user from the general list to avoid duplicates
          const otherUsers = users.filter(
            (user) =>
              !invitedUsers.some((invited) => invited.user_id === user.id) &&
              user.id != currentUserId
          );

          // Add other users
          finalUserList = [...finalUserList, ...otherUsers];

          setAvailableUsers(finalUserList);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [data.invitedUsers]);

  const currentPar = pars[currentHole - 1];

  // Tailwind class definitions
  const classes = {
    container:
      "max-w-6xl w-full mx-auto bg-white rounded-2xl shadow-lg font-sans mt-6 px-4 sm:px-6 lg:px-8 py-6 lg:py-8",
    header: "border-b-2 pb-4 mb-6",
    headerTitle: "text-3xl font-semibold text-gray-800 mb-2",
    headerSubtitle: "text-lg text-gray-600",
    sectionTitle: "text-2xl font-semibold text-gray-800 mb-4",
    playerSelectionRow: "grid grid-cols-1 md:grid-cols-2 gap-5 mb-6",
    playerSelectGroup: "flex flex-col gap-2",
    label: "font-medium text-gray-700",
    holeSection:
      "bg-gray-50 rounded-xl p-5 mb-6 shadow-sm border border-gray-200",
    holeTitle: "text-2xl font-semibold text-gray-800 mt-0 mb-4",
    guidanceBox:
      "bg-blue-50 border border-blue-200 rounded-lg p-5 shadow-sm mb-5",
    guidanceTitle:
      "text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2",
    guidanceContent: "text-blue-700 space-y-2 text-sm leading-relaxed",
    formGroup: "mb-4 flex items-center",
    select:
      "px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    timer: "flex items-center gap-3 mb-4",
    timerText: "text-base font-medium text-gray-700",
    timerButton:
      "px-4 py-2 rounded-lg text-white font-medium text-sm transition-all duration-200",
    tip: "bg-blue-50 text-blue-700 p-3 rounded-lg border border-blue-200 italic mb-4",
    playersContainer: "grid grid-cols-1 md:grid-cols-2 gap-5 mb-6",
    playerCard: "flex-1 p-5 rounded-xl shadow-sm transition-all duration-300",
    playerName: "text-lg font-semibold text-gray-800 mt-0 mb-2 text-center",
    scoreText: "text-base text-gray-600 mb-2 text-center",
    totalScore: "text-lg font-semibold text-gray-800 text-center",
    actionsContainer: "flex flex-wrap gap-3 mb-6",
    primaryButton:
      "bg-[#3498db] hover:bg-[#2980b9] text-white font-medium px-4 py-2 rounded-lg transition-all duration-200",
    successButton:
      "bg-emerald-500 hover:bg-emerald-600 text-white font-medium px-4 py-2 rounded-lg transition-all duration-200",
    warningButton:
      "bg-orange-500 hover:bg-orange-600 text-white font-medium px-4 py-2 rounded-lg transition-all duration-200",
    dangerButton:
      "bg-red-500 hover:bg-red-600 text-white font-medium px-4 py-2 rounded-lg transition-all duration-200",
    saveButton:
      "w-full max-w-[260px] mx-auto block bg-emerald-500 text-white border-2 border-emerald-500 font-semibold py-3 px-4 rounded-lg shadow-md transition-all duration-200 mt-4 hover:bg-white hover:text-emerald-600 hover:-translate-y-0.5",
    checkboxContainer: "flex items-center gap-2 ml-3",
    checkbox: "w-4 h-4 cursor-pointer",
    checkboxLabel: "text-sm text-gray-700 cursor-pointer",
    leaderboardSection: "mt-8",
    leaderboardTitle: "text-xl font-semibold text-gray-800 mb-4",
    tableWrapper: "w-full overflow-x-auto",
    table:
      "w-full text-sm border-collapse mt-4 rounded-lg overflow-hidden shadow-sm",
    tableHeader:
      "bg-gray-100 text-gray-800 font-semibold p-3 text-center border-b border-gray-200",
    tableCell: "p-3 border-b border-gray-200 bg-white text-center",
    matchCompleteSection:
      "bg-emerald-50 rounded-xl p-6 mt-8 shadow-sm border border-emerald-200 text-center",
    matchCompleteTitle: "text-2xl font-semibold text-gray-800 mt-0 mb-4",
    winnerText: "text-lg text-gray-600 mb-5",
    winnerName: "font-bold text-gray-800",
  };

  useEffect(() => {
    if (isTiming) {
      timerRef.current = setInterval(() => setTimer((t) => t + 1), 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isTiming]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? "0" + s : s}`;
  };

  const handleStroke = () => {
    if (currentHole > 18) return;
    const updatePlayer = activePlayer === "player1" ? setPlayer1 : setPlayer2;
    const player = activePlayer === "player1" ? player1 : player2;
    const newStrokes = [...player.strokes];
    newStrokes[currentHole - 1] = (newStrokes[currentHole - 1] || 0) + 1;
    updatePlayer({ ...player, strokes: newStrokes });
    setHistory((h) => [...h, { hole: currentHole, player: activePlayer }]);
    if (autoSwitch) switchPlayer();
  };

  const undoStroke = () => {
    const last = history.pop();
    if (!last) return;
    const updatePlayer = last.player === "player1" ? setPlayer1 : setPlayer2;
    const player = last.player === "player1" ? player1 : player2;
    const newStrokes = [...player.strokes];
    if (newStrokes[last.hole - 1] > 0) newStrokes[last.hole - 1]--;
    updatePlayer({ ...player, strokes: newStrokes });
    setHistory([...history]);
  };

  const handleFinishHole = () => {
    const p1 = player1.strokes[currentHole - 1] || 0;
    const p2 = player2.strokes[currentHole - 1] || 0;
    setPlayer1((prev) => ({ ...prev, total: prev.total + p1 }));
    setPlayer2((prev) => ({ ...prev, total: prev.total + p2 }));
    setCurrentHole((prev) => prev + 1);
    setActivePlayer("player1");
    setTimer(0);
    setIsTiming(false);
  };

  const switchPlayer = () => {
    setActivePlayer((prev) => (prev === "player1" ? "player2" : "player1"));
  };

  const handleParChange = (e) => {
    const newPars = [...pars];
    newPars[currentHole - 1] = Number.parseInt(e.target.value);
    setPars(newPars);
  };

  const handleSaveMatch = async () => {
    // Validate match before saving
    const validationErrors = validateGolfMatch();
    if (validationErrors.length > 0) {
      toast.error(
        "Please fix the following issues before saving:\n\n" +
          validationErrors.join("\n")
      );
      return;
    }

    const arenaBookingId =
      data.arenaBooking?.id === "0" ? null : data.arenaBooking?.id;

    const bodyData = {
      arenaBookingId,
      sport: "Golf",
      games: [
        {
          gameMode,
          player1: {
            ...player1,
            playerId: player1.playerId || null, // Ensure playerId is saved
          },
          player2: {
            ...player2,
            playerId: player2.playerId || null, // Ensure playerId is saved
          },
          pars,
          holes: currentHole - 1,
          winner:
            player1.total === player2.total
              ? "draw"
              : player1.total < player2.total
              ? "player1"
              : "player2",
        },
      ],
    };

    // Add either userId or instituteId based on context
    if (isInstitute) {
      bodyData.instituteId = instituteId;
    } else {
      bodyData.userId = user?.uid;
    }

    if (!bodyData.userId && !bodyData.instituteId) {
      toast.error("User not logged in.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/user-add-game/api/selection/save-match`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(bodyData),
        }
      );

      if (response.ok) {
        toast.success("Golf match saved!");

        // Navigate based on context
        if (isInstitute) {
          navigate("/institute-owner");
        } else {
          navigate("/Manage");
        }
      } else {
        const error = await response.json();
        console.error("Save error:", error);
        toast.error("Failed to save golf match.");
      }
    } catch (err) {
      console.error("Error during save:", err);
      toast.error("An error occurred while saving.");
    }
  };

  // Validate golf match
  const validateGolfMatch = () => {
    const errors = [];

    // Check if both players are selected
    if (!player1.playerId || !player2.playerId) {
      errors.push("Both players must be selected");
    }

    // Check if same player is selected for both positions
    if (
      player1.playerId &&
      player2.playerId &&
      player1.playerId === player2.playerId
    ) {
      errors.push("Cannot select the same player for both positions");
    }

    // Check if at least one hole has been played
    if (currentHole <= 1) {
      errors.push("At least one hole must be completed");
    }

    // Check if current hole has strokes recorded for both players
    if (currentHole <= 18) {
      const p1Strokes = player1.strokes[currentHole - 1] || 0;
      const p2Strokes = player2.strokes[currentHole - 1] || 0;

      if (p1Strokes === 0 || p2Strokes === 0) {
        errors.push(
          `Hole ${currentHole}: Both players must record strokes before finishing the hole`
        );
      }

      // Check for reasonable stroke counts (1-15 per hole)
      if (p1Strokes > 15 || p2Strokes > 15) {
        errors.push(
          `Hole ${currentHole}: Stroke count seems unreasonably high (max 15 per hole)`
        );
      }
    }

    // Validate completed holes
    for (let i = 0; i < currentHole - 1; i++) {
      const p1Strokes = player1.strokes[i] || 0;
      const p2Strokes = player2.strokes[i] || 0;

      if (p1Strokes === 0 || p2Strokes === 0) {
        errors.push(
          `Hole ${i + 1}: Missing stroke data for one or both players`
        );
      }

      if (p1Strokes > 15 || p2Strokes > 15) {
        errors.push(
          `Hole ${
            i + 1
          }: Stroke count seems unreasonably high (max 15 per hole)`
        );
      }
    }

    return errors;
  };

  const isPlayer1Leading = player1.total < player2.total;
  const isPlayer2Leading = player2.total < player1.total;

  return (
    <>
      <HeaderMain pageType="AddGame" />
      <div className={classes.container}>
        <div className={classes.header} style={{ borderColor: brandBlue }}>
          <h2 className={classes.headerTitle}>
            Golf Match at {data.arenaBooking?.name}
          </h2>
          <p className={classes.headerSubtitle}>
            {data.arenaBooking?.date && data.arenaBooking?.time && (
              <>
                Booking: {data.arenaBooking.date} at {data.arenaBooking.time}
              </>
            )}
          </p>
        </div>

        {/* Player Selection */}
        <div className="mb-6">
          <h3 className={classes.sectionTitle}>Select Players</h3>
          <div className={classes.playerSelectionRow}>
            <div className={classes.playerSelectGroup}>
              <label className={classes.label}>Player 1:</label>
              <PlayerSlot
                availableUsers={availableUsers}
                selectedPlayerId={player1.playerId}
                selectedPlayerName={player1.name}
                excludeUserId={player2.playerId}
                onSelectPlayer={(userInfo, customName) => {
                  setPlayer1({
                    ...player1,
                    playerId:
                      userInfo?.id ||
                      userInfo?.user_id ||
                      `custom_${Date.now()}`,
                    name: userInfo?.full_name || customName || "Guest Player",
                  });
                }}
              />
            </div>
            <div className={classes.playerSelectGroup}>
              <label className={classes.label}>Player 2:</label>
              <PlayerSlot
                availableUsers={availableUsers}
                selectedPlayerId={player2.playerId}
                selectedPlayerName={player2.name}
                excludeUserId={player1.playerId}
                onSelectPlayer={(userInfo, customName) => {
                  setPlayer2({
                    ...player2,
                    playerId:
                      userInfo?.id ||
                      userInfo?.user_id ||
                      `custom_${Date.now()}`,
                    name: userInfo?.full_name || customName || "Guest Player",
                  });
                }}
              />
            </div>
          </div>
        </div>

        {currentHole <= 18 && (
          <div className={classes.holeSection}>
            <h3 className={classes.holeTitle}>Hole {currentHole}</h3>

            {/* Golf Scoring Guidance */}
            <div className={classes.guidanceBox}>
              <h4 className={classes.guidanceTitle}>⛳ Golf Scoring Guide</h4>
              <div className={classes.guidanceContent}>
                <p>
                  <strong>Current Hole:</strong> {currentHole} of 18 (Par{" "}
                  {currentPar})
                </p>
                <p>
                  <strong>Scoring:</strong> Eagle (-2) | Birdie (-1) | Par (0) |
                  Bogey (+1) | Double Bogey (+2)
                </p>
                <p>
                  <strong>Goal:</strong> Complete each hole in as few strokes as
                  possible
                </p>
                <p>
                  <strong>Winner:</strong> Player with the lowest total strokes
                  after 18 holes
                </p>
                <p>
                  <strong>Tip:</strong> Record each stroke accurately - click
                  "+1 Stroke" for the active player
                </p>
              </div>
            </div>

            <div className={classes.formGroup}>
              <label className={classes.label}>Par:</label>
              <select
                value={currentPar}
                onChange={handleParChange}
                className={classes.select}
              >
                {[3, 4, 5].map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <div className={classes.timer}>
              <p className={classes.timerText}>⏱ Time: {formatTime(timer)}</p>
              <button
                onClick={() => setIsTiming(!isTiming)}
                className={`${classes.timerButton} ${
                  isTiming
                    ? "bg-red-500 hover:bg-red-600"
                    : "bg-[#3498db] hover:bg-[#2980b9]"
                }`}
              >
                {isTiming ? "Stop Timer" : "Start Timer"}
              </button>
            </div>
            <p className={classes.tip}>
              {tips[(currentHole - 1) % tips.length]}
            </p>
          </div>
        )}

        <div className={classes.playersContainer}>
          {["player1", "player2"].map((key) => {
            const player = key === "player1" ? player1 : player2;
            const strokes = player.strokes[currentHole - 1] || 0;
            const status = getScoreStatus(strokes, currentPar);
            const isLeading =
              key === "player1" ? isPlayer1Leading : isPlayer2Leading;
            const isActive = activePlayer === key;

            return (
              <div
                key={key}
                className={`${classes.playerCard} ${
                  isActive
                    ? "border-3 border-emerald-500 bg-emerald-50"
                    : "border border-gray-200 bg-white"
                } ${isLeading && !isActive ? "bg-emerald-50" : ""}`}
              >
                <h4 className={classes.playerName}>{player.name}</h4>
                <p className={classes.scoreText}>
                  Hole Score: {strokes} ({status})
                </p>
                <p className={classes.totalScore}>
                  Total Strokes: {player.total}
                </p>
              </div>
            );
          })}
        </div>

        {currentHole <= 18 && (
          <div className={classes.actionsContainer}>
            <button onClick={handleStroke} className={classes.primaryButton}>
              +1 Stroke (
              {activePlayer === "player1" ? player1.name : player2.name})
            </button>
            <button onClick={switchPlayer} className={classes.warningButton}>
              Switch Player
            </button>
            <button
              onClick={handleFinishHole}
              className={classes.successButton}
            >
              Finish Hole
            </button>
            <button onClick={undoStroke} className={classes.dangerButton}>
              Undo Stroke
            </button>
            <div className={classes.checkboxContainer}>
              <input
                type="checkbox"
                checked={autoSwitch}
                onChange={() => setAutoSwitch((v) => !v)}
                className={classes.checkbox}
                id="autoSwitch"
              />
              <label htmlFor="autoSwitch" className={classes.checkboxLabel}>
                Auto-switch player
              </label>
            </div>
          </div>
        )}

        <div className={classes.leaderboardSection}>
          <h4 className={classes.leaderboardTitle}>Hole Leaderboard</h4>
          <div className={classes.tableWrapper}>
            <table className={classes.table}>
              <thead>
                <tr>
                  <th className={classes.tableHeader}>Hole</th>
                  <th className={classes.tableHeader}>{player1.name}</th>
                  <th className={classes.tableHeader}>{player2.name}</th>
                  <th className={classes.tableHeader}>Par</th>
                  <th className={classes.tableHeader}>Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: currentHole - 1 }, (_, i) => (
                  <tr key={i}>
                    <td className={classes.tableCell}>{i + 1}</td>
                    <td className={classes.tableCell}>{player1.strokes[i]}</td>
                    <td className={classes.tableCell}>{player2.strokes[i]}</td>
                    <td className={classes.tableCell}>{pars[i]}</td>
                    <td className={classes.tableCell}>
                      {getScoreStatus(player1.strokes[i], pars[i])} /{" "}
                      {getScoreStatus(player2.strokes[i], pars[i])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {currentHole <= 18 && (
          <button onClick={handleSaveMatch} className={classes.saveButton}>
            Save Game
          </button>
        )}

        {currentHole > 18 && (
          <div className={classes.matchCompleteSection}>
            <h3 className={classes.matchCompleteTitle}>Match Complete!</h3>
            <p className={classes.winnerText}>
              Winner:{" "}
              <span className={classes.winnerName}>
                {player1.total === player2.total
                  ? "Draw"
                  : player1.total < player2.total
                  ? player1.name
                  : player2.name}
              </span>
            </p>
            <button onClick={handleSaveMatch} className={classes.saveButton}>
              Save Golf Match
            </button>
          </div>
        )}
      </div>
    </>
  );
};

// PlayerSlot component for single player selection per slot
const PlayerSlot = ({
  availableUsers,
  selectedPlayerId,
  selectedPlayerName,
  excludeUserId,
  onSelectPlayer,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [customName, setCustomName] = useState("");
  const [useCustomName, setUseCustomName] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const isSlotEmpty =
    !selectedPlayerId ||
    !selectedPlayerName ||
    selectedPlayerName === "Player 1" ||
    selectedPlayerName === "Player 2";

  // Fetch user suggestions from API
  const fetchUserSuggestions = async (term) => {
    if (!term || term.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/common/search-users?term=${term}`
      );
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
      }
    } catch (error) {
      console.error("Error fetching user suggestions:", error);
      setSuggestions([]);
    }
  };

  // Debounced search
  useEffect(() => {
    if (!useCustomName && searchTerm) {
      const timer = setTimeout(() => {
        fetchUserSuggestions(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSuggestions([]);
    }
  }, [searchTerm, useCustomName]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setShowSuggestions(true);
    setSelectedUser(null);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchTerm(user.full_name || user.username);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const handleSave = () => {
    if (useCustomName && customName.trim()) {
      onSelectPlayer(null, customName.trim());
      setCustomName("");
      setIsEditing(false);
    } else if (!useCustomName && selectedUser) {
      onSelectPlayer(selectedUser);
      setSelectedUser(null);
      setSearchTerm("");
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setSelectedUser(null);
    setSearchTerm("");
    setCustomName("");
    setUseCustomName(false);
    setSuggestions([]);
    setShowSuggestions(false);
    setIsEditing(false);
  };

  const styles = {
    container: {
      display: "flex",
      flexDirection: "column",
      gap: "8px",
    },
    selectedPlayer: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      padding: "8px 12px",
      backgroundColor: "#f7fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      fontSize: "14px",
      color: "#2c3e50",
    },
    editButton: {
      backgroundColor: "#f39c12",
      color: "white",
      border: "none",
      padding: "4px 8px",
      borderRadius: "4px",
      fontSize: "12px",
      cursor: "pointer",
    },
    searchContainer: {
      position: "relative",
      width: "100%",
    },
    input: {
      padding: "8px",
      borderRadius: "4px",
      border: "1px solid #cbd5e0",
      fontSize: "14px",
      width: "100%",
    },
    suggestionsDropdown: {
      position: "absolute",
      top: "100%",
      left: 0,
      right: 0,
      backgroundColor: "white",
      border: "1px solid #cbd5e0",
      borderTop: "none",
      borderRadius: "0 0 4px 4px",
      maxHeight: "200px",
      overflowY: "auto",
      zIndex: 1000,
      boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    },
    suggestionItem: {
      padding: "8px 12px",
      cursor: "pointer",
      borderBottom: "1px solid #f0f0f0",
      fontSize: "14px",
      transition: "background-color 0.2s",
    },
    suggestionItemDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
      backgroundColor: "#f0f0f0",
    },
    button: {
      backgroundColor: "#3498db",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      cursor: "pointer",
    },
    cancelButton: {
      backgroundColor: "#95a5a6",
      color: "white",
      border: "none",
      padding: "6px 12px",
      borderRadius: "4px",
      fontSize: "12px",
      cursor: "pointer",
    },
    toggle: {
      fontSize: "12px",
      color: "#3498db",
      cursor: "pointer",
      textDecoration: "underline",
    },
    emptySlot: {
      padding: "12px",
      border: "2px dashed #cbd5e0",
      borderRadius: "6px",
      textAlign: "center",
      color: "#718096",
      fontSize: "14px",
      cursor: "pointer",
      backgroundColor: "#f8fafc",
    },
    noResults: {
      padding: "8px 12px",
      fontSize: "14px",
      color: "#718096",
      textAlign: "center",
    },
  };

  // Tailwind styles - convert from inline styles
  const slotClasses = {
    container: "flex flex-col gap-2",
    selectedPlayer:
      "flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800",
    editButton:
      "bg-orange-500 hover:bg-orange-600 text-white px-2 py-1 rounded text-xs transition-colors",
    searchContainer: "relative",
    input: "px-2 py-2 border border-gray-300 rounded w-full text-sm",
    suggestionsDropdown:
      "absolute top-full left-0 right-0 bg-white border border-gray-300 border-t-0 rounded-b max-h-48 overflow-y-auto z-50 shadow-lg",
    suggestionItem:
      "px-3 py-2 cursor-pointer border-b border-gray-100 text-sm transition-colors hover:bg-gray-50",
    suggestionItemDisabled: "opacity-50 cursor-not-allowed bg-gray-50",
    noResults: "px-3 py-2 text-sm text-gray-500",
    button:
      "bg-[#3498db] hover:bg-[#2980b9] text-white px-3 py-1 rounded text-xs transition-colors disabled:opacity-50",
    cancelButton:
      "bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded text-xs transition-colors",
    toggle:
      "text-xs text-blue-500 cursor-pointer underline hover:text-blue-600",
    emptySlot:
      "px-3 py-3 border-2 border-dashed border-gray-300 rounded-lg text-center text-gray-500 text-sm cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors",
  };

  // Show editing UI if editing or no player selected
  if (isEditing || isSlotEmpty) {
    return (
      <div className={slotClasses.container}>
        <div className="flex flex-col gap-2">
          {!useCustomName ? (
            <>
              <div className={slotClasses.searchContainer}>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => {
                    // Delay to allow click on suggestion
                    setTimeout(() => setShowSuggestions(false), 200);
                  }}
                  placeholder="Search for system users..."
                  className={slotClasses.input}
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className={slotClasses.suggestionsDropdown}>
                    {suggestions.map((user) => {
                      const userId = user.id || user.user_id;
                      const isAlreadySelected =
                        excludeUserId &&
                        (typeof excludeUserId === "string" &&
                        excludeUserId.startsWith("custom_")
                          ? false
                          : excludeUserId === userId);
                      return (
                        <div
                          key={userId}
                          className={`${slotClasses.suggestionItem} ${
                            isAlreadySelected
                              ? slotClasses.suggestionItemDisabled
                              : ""
                          }`}
                          onClick={() =>
                            !isAlreadySelected && handleSelectUser(user)
                          }
                        >
                          <div className="font-medium">
                            {user.full_name || user.username}
                            {isAlreadySelected && (
                              <span className="text-red-500 ml-2">
                                (Already Added)
                              </span>
                            )}
                          </div>
                          {user.email && (
                            <div className="text-xs text-gray-500">
                              {user.email}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {showSuggestions &&
                  searchTerm.length >= 2 &&
                  suggestions.length === 0 && (
                    <div className={slotClasses.suggestionsDropdown}>
                      <div className={slotClasses.noResults}>
                        No users found
                      </div>
                    </div>
                  )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={!selectedUser}
                  className={slotClasses.button}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className={slotClasses.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="Enter custom player name"
                className={slotClasses.input}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSave}
                  disabled={!customName.trim()}
                  className={slotClasses.button}
                >
                  Save
                </button>
                <button
                  onClick={handleCancel}
                  className={slotClasses.cancelButton}
                >
                  Cancel
                </button>
              </div>
            </>
          )}
          <span
            onClick={() => setUseCustomName(!useCustomName)}
            className={slotClasses.toggle}
          >
            {useCustomName ? "Use system users" : "Add custom name"}
          </span>
        </div>
      </div>
    );
  }

  // If player is selected and not editing, show the selected player
  return (
    <div className={slotClasses.container}>
      <div className={slotClasses.selectedPlayer}>
        <span>{selectedPlayerName}</span>
        <button
          onClick={() => setIsEditing(true)}
          className={slotClasses.editButton}
        >
          Edit
        </button>
      </div>
    </div>
  );
};
